import { SLOTS, type SignalData } from '../lib/rtc';
import type { Socket } from '../lib/socket';

export interface Viewer {
  connId: string;
  parentName: string;
  talking: boolean;
  video: boolean;
}

interface Peer {
  pc: RTCPeerConnection;
  slots: RTCRtpTransceiver[];
  viewer: Viewer;
  queue: Promise<void>;
  voice: HTMLAudioElement;
  parentVideo: MediaStream;
}

export interface LocalTracks {
  screen: MediaStreamTrack | null;
  camera: MediaStreamTrack | null;
  mic: MediaStreamTrack | null;
  audio: MediaStreamTrack | null;
}

/**
 * The child's side of live view: one peer connection per parent watching. The parent offers the
 * fixed slots from lib/rtc.ts; this side answers, sending its tracks in the first four and receiving
 * the parent's voice and camera in the last two.
 */
export class AgentPeers {
  private peers = new Map<string, Peer>();
  private tracks: LocalTracks = { screen: null, camera: null, mic: null, audio: null };

  constructor(
    private socket: Socket,
    private iceServers: () => Promise<RTCIceServer[]>,
    private onChange: (viewers: Viewer[], parentVideo: MediaStream | null) => void,
  ) {}

  get viewers(): Viewer[] {
    return [...this.peers.values()].map((p) => p.viewer);
  }

  signal(from: string, parentName: string, data: SignalData) {
    if (data.description?.type === 'offer') {
      this.close(from, false);
      const peer = this.create(from, parentName);
      peer.queue = peer.queue.then(() => this.answer(peer, data.description!));
      return;
    }
    const peer = this.peers.get(from);
    if (!peer || !data.candidate) return;
    peer.queue = peer.queue.then(() => peer.pc.addIceCandidate(data.candidate!).catch(() => undefined));
  }

  talk(from: string, on: boolean, video: boolean) {
    const peer = this.peers.get(from);
    if (!peer) return;
    peer.viewer = { ...peer.viewer, talking: on, video };
    peer.voice.muted = !on;
    this.changed();
  }

  setTracks(tracks: LocalTracks) {
    this.tracks = tracks;
    for (const peer of this.peers.values()) this.fill(peer);
  }

  close(connId: string, tell = true, reason = '') {
    const peer = this.peers.get(connId);
    if (!peer) return;
    this.peers.delete(connId);
    peer.pc.close();
    peer.voice.srcObject = null;
    if (tell) this.socket.send({ type: 'hangup', to: connId, reason });
    this.changed();
  }

  closeAll(reason = '') {
    for (const id of [...this.peers.keys()]) this.close(id, true, reason);
  }

  private create(connId: string, parentName: string): Peer {
    const pc = new RTCPeerConnection();
    const voice = new Audio();
    voice.autoplay = true;
    voice.muted = true;
    const peer: Peer = {
      pc,
      slots: [],
      viewer: { connId, parentName: parentName || 'Your parent', talking: false, video: false },
      queue: Promise.resolve(),
      voice,
      parentVideo: new MediaStream(),
    };
    pc.onicecandidate = (e) =>
      this.socket.send({ type: 'signal', to: connId, data: { candidate: e.candidate ? e.candidate.toJSON() : null } });
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') this.close(connId, false);
    };
    this.peers.set(connId, peer);
    this.changed();
    return peer;
  }

  private async answer(peer: Peer, offer: RTCSessionDescriptionInit) {
    try {
      peer.pc.setConfiguration({ iceServers: await this.iceServers() });
      await peer.pc.setRemoteDescription(offer);
      peer.slots = peer.pc.getTransceivers();
      if (peer.slots.length < SLOTS.length) throw new Error('Unexpected offer');
      SLOTS.forEach((slot, i) => {
        peer.slots[i]!.direction = slot.from === 'child' ? 'sendonly' : 'recvonly';
      });
      await this.fill(peer);
      peer.voice.srcObject = new MediaStream([peer.slots[4]!.receiver.track]);
      peer.voice.play().catch(() => undefined);
      peer.parentVideo = new MediaStream([peer.slots[5]!.receiver.track]);
      await peer.pc.setLocalDescription(await peer.pc.createAnswer());
      this.socket.send({ type: 'signal', to: peer.viewer.connId, data: { description: peer.pc.localDescription!.toJSON() } });
    } catch (e) {
      console.warn('answer failed', e);
      this.close(peer.viewer.connId, true, 'The child’s computer couldn’t start the live view.');
    }
  }

  private async fill(peer: Peer) {
    if (peer.slots.length < 4) return;
    const t = this.tracks;
    await Promise.all([t.screen, t.camera, t.mic, t.audio].map((track, i) => peer.slots[i]!.sender.replaceTrack(track).catch(() => undefined)));
  }

  private changed() {
    const showing = [...this.peers.values()].find((p) => p.viewer.video);
    this.onChange(this.viewers, showing ? showing.parentVideo : null);
  }
}
